import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { IcBlockIcon, IcInfoIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';
import { IPopover, IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { ReportUserPopup } from 'APP/main/PopupManager/Popups/ReportUserPopup/ReportUserPopup';
import { Features } from 'APP/packages/features/Features';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';
import type { User } from 'STORE/Users/User/User';

interface IUnknownUserBlockPresenterParams {
  userId: string;
  groupId: string;
  popover: IPopover;
}

interface IUnknownUserBlockPresenter {
  childPopup: PopupStore | null;
  menuItems: IPopoverMenuItem[];
  user: User | null;
  onBlockUser(): Promise<void>;
  onBlockAndReportUser(): void;
}

export const useUnknownUserBlockPresenter = (
  data: IUnknownUserBlockPresenterParams
): IUnknownUserBlockPresenter => {
  const { userId, groupId, popover } = data;
  const { t } = useTranslation();
  const navigateTo = useNavigateTo();
  const source = useAsObservableSource({ groupId, userId, navigateTo });

  const presenter = useLocalStore<IUnknownUserBlockPresenter>(() => ({
    childPopup: null,

    get user(): User | null {
      return Entities.usersStore.getUserById(source.userId);
    },

    get menuItems(): IPopoverMenuItem[] {
      const items = [
        {
          key: 'block',
          icon: IcBlockIcon,
          title: t('block_userprofile'),
          isVisible: true,
          onClick: presenter.onBlockUser,
        },
        {
          key: 'block-and-report',
          icon: IcInfoIcon,
          title: t('common_block_and_report'),
          isVisible: Features.isReportsEnabled(),
          onClick: presenter.onBlockAndReportUser,
        },
      ];

      return items.filter((item) => item.isVisible);
    },

    onBlockAndReportUser(): void {
      presenter.onBlockUser();
      Tasks.app.openPopup(ReportUserPopup, {
        params: { userId: source.userId, groupId: source.groupId },
      });
    },

    async onBlockUser(): Promise<void> {
      await Tasks.relationships.blockUser(source.userId);
      popover.close();
      presenter.user?.setIsUnknown(false);
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
};
